import type { ClientInstanceOf } from '@elara/client-core/firebase/types';
import type { TripModel } from '@elara/client-core/models/TripModel';

import React from 'react';

import { useProductTourState, useToastState, useTripsState } from '#states/hooks.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  MaterialIcon,
  ToastAction,
} from '#ui/index.js';

interface TripSettingsActionsProps {
  trip: ClientInstanceOf<TripModel>;
  trigger: React.ReactNode;
  menuPosition?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * TripPageActions component - handles trip management actions like rename and delete
 */
export function TripSettingsActions({
  trip,
  trigger,
  menuPosition = 'bottom',
}: TripSettingsActionsProps): React.ReactNode {
  const tripsState = useTripsState();
  const toastState = useToastState();
  const productTourState = useProductTourState();
  const currentTrip = tripsState.current;

  if (!trip) return undefined;

  function handleDeleteTrip() {
    setTimeout(() => tripsState.openDeleteDialog(), 50);
  }

  function handleOpenDateDialog() {
    setTimeout(
      () => tripsState.openDateDialog(currentTrip?.startDate ?? undefined, currentTrip?.endDate ?? undefined),
      50,
    );
  }

  function handleRenameTrip() {
    setTimeout(() => tripsState.openRenameDialog(trip.displayName), 50);
  }

  async function handleResetDemoTrip() {
    if (!trip.id) return;
    await productTourState.resetDemoTrip(trip.id);
  }

  async function confirmDeleteTrip() {
    if (!trip.id) return;

    const tripId = trip.id;
    const tripTitle = trip.displayName;

    tripsState.closeDeleteDialog(); // Close modal immediately

    // Show "deleting" toast immediately
    const deletingToastId = toastState.addToast({
      title: 'Deleting trip...',
      description: `${tripTitle} is being deleted`,
    });

    try {
      await tripsState.deleteTrip(tripId);

      // Remove the "deleting" toast and show success toast with undo action
      toastState.removeToast(deletingToastId);
      toastState.addToast({
        title: 'Trip deleted',
        variant: 'success',
        description: `${tripTitle} has been deleted`,
        action: (
          <ToastAction
            altText="Restore trip"
            onClick={async () => {
              try {
                await tripsState.restoreTrip(tripId);
                toastState.addToast({
                  title: 'Trip restored',
                  description: `${tripTitle} has been restored`,
                  variant: 'success',
                });
              } catch (error) {
                console.error('Failed to restore trip:', error);
                toastState.addToast({
                  title: 'Failed to restore trip',
                  description: 'Please try again',
                  variant: 'destructive',
                });
              }
            }}
          >
            Undo
          </ToastAction>
        ),
      });
    } catch (error) {
      console.error('Failed to delete trip:', error);

      // Remove the "deleting" toast and show error toast
      toastState.removeToast(deletingToastId);
      toastState.addToast({ title: 'Failed to delete trip', description: 'Please try again.', variant: 'destructive' });
    }
  }

  return (
    <>
      <DropdownMenu key={`${trip.id}-dropdown-menu`}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent side={menuPosition} align={menuPosition === 'right' ? 'start' : 'center'}>
          <DropdownMenuItem onClick={handleRenameTrip} className="flex items-center gap-2">
            <MaterialIcon icon="edit" />
            <span>Rename trip</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleOpenDateDialog} className="flex items-center gap-2">
            <MaterialIcon icon="date_range" />
            <span>Update dates</span>
          </DropdownMenuItem>
          {currentTrip?.id === trip.id && tripsState.canShareItinerary && (
            <DropdownMenuItem
              onClick={() => setTimeout(() => tripsState.openShareDialog(), 50)}
              className="flex items-center gap-2"
            >
              <MaterialIcon icon="ios_share" />
              <span>Share itinerary</span>
            </DropdownMenuItem>
          )}
          {trip.isDemo && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setTimeout(() => void handleResetDemoTrip(), 50)}
                className="flex items-center gap-2"
              >
                <MaterialIcon icon="restart_alt" />
                <span>Reset demo trip</span>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={() => {
              // Close dropdown first, then open modal after a small delay
              setTimeout(() => handleDeleteTrip(), 50);
            }}
            className="text-destructive"
          >
            <MaterialIcon icon="delete" className="mr-2" />
            Delete trip
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={tripsState.isDeleteDialogOpen} onOpenChange={(open) => !open && tripsState.closeDeleteDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure you want to delete {trip.displayName}?</DialogTitle>
            <DialogDescription>This will remove all the messages associated with this trip.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => tripsState.closeDeleteDialog()}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTrip}>
              Delete trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
